/**
 * Closures admin panel — lets admins flag days as company-wide
 * closures (e.g. national holidays, Christmas shutdown). Lives at the
 * top of the Dashboard view, above the KPI tiles, since that's the
 * admin-only surface of the app.
 *
 * Reads + writes go directly to the closures table; realtime
 * propagates the change to every other open tab. RLS gates writes
 * server-side (closures_admin_write).
 */
import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { state } from "../state/store";
import { toast } from "../utils/toast";
import { restUpsert, restDelete } from "../utils/withRetry";
import { formatDayLabel } from "../utils/dates";

/** Persist the panel's collapsed/expanded choice. Default = collapsed, so a
 *  long closures list doesn't push the dashboard KPIs down the page. */
const LS_CLOSURES_OPEN_KEY = "icons_closures_open_v1";

export function ClosuresPanel(): JSX.Element {
  const s = state.value;
  const [day, setDay] = useState("");
  const [endDay, setEndDay] = useState(""); // optional range end → a closure period
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_CLOSURES_OPEN_KEY) === "1"; } catch { return false; }
  });
  const toggleOpen = (): void =>
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem(LS_CLOSURES_OPEN_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });

  // Sort closures: future first (chronological), then past (reverse chronological).
  // Lets admins see what's coming up at a glance, with history below.
  const today = new Date().toISOString().slice(0, 10);
  const all = Object.entries(s.closures ?? {}).map(([d, l]) => ({ day: d, label: l }));
  const upcoming = all.filter((c) => c.day >= today).sort((a, b) => a.day.localeCompare(b.day));
  const past = all.filter((c) => c.day < today).sort((a, b) => b.day.localeCompare(a.day));
  const summary =
    all.length === 0 ? "none yet" : `${upcoming.length} upcoming · ${past.length} past`;

  // Both add and remove use raw fetch (restUpsert / restDelete) for the
  // same reason as Directory's toggleAdmin: bypass the SDK's auth-token
  // lock that the realtime channel contests under load. Closures get
  // edited frequently during testing; SDK lock contention here was
  // contributing to the "nothing happens when I click" reports.
  const add = async (e: Event): Promise<void> => {
    e.preventDefault();
    if (!day) {
      toast("Pick a start date");
      return;
    }
    // Single day if "To" is blank; otherwise the whole period [start, end]
    // inclusive (e.g. a closure week). Tolerate a reversed range.
    let start = day;
    let end = endDay || day;
    if (end < start) [start, end] = [end, start];
    // Build one closure row per calendar day. Iterate at NOON and format
    // from local components so a timezone/DST offset can never shift the
    // date (which toISOString() would). Capped at 100 days as a guard.
    const rows: Array<{ day: string; label: string }> = [];
    const cur = new Date(`${start}T12:00:00`);
    const last = new Date(`${end}T12:00:00`);
    const lbl = label.trim();
    while (cur <= last && rows.length < 100) {
      const k = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
      rows.push({ day: k, label: lbl });
      cur.setDate(cur.getDate() + 1);
    }
    if (rows.length === 0) {
      toast("Pick a valid date range");
      return;
    }
    setBusy(true);
    try {
      await restUpsert("closures", rows);
      setDay("");
      setEndDay("");
      setLabel("");
      toast(
        rows.length === 1
          ? `Closure saved: ${formatDayLabel(start)}`
          : `${rows.length} closure days saved (${formatDayLabel(start)} → ${formatDayLabel(end)})`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast(`Could not save: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (closureDay: string): Promise<void> => {
    const c = s.closures[closureDay];
    const niceLabel = c ? `"${c}" on ${formatDayLabel(closureDay)}` : formatDayLabel(closureDay);
    if (!confirm(`Remove the closure ${niceLabel}?\n\nThis lets the day appear as a normal working day again.`)) {
      return;
    }
    try {
      await restDelete("closures", `day=eq.${encodeURIComponent(closureDay)}`);
      toast("Closure removed");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast(`Could not remove: ${msg}`);
    }
  };

  return (
    <section class="closures-panel" aria-labelledby="closuresTitle">
      <h3 id="closuresTitle" class="closures-head">
        <button
          type="button"
          class="closures-toggle"
          aria-expanded={open}
          onClick={toggleOpen}
        >
          <span class="closures-chevron" aria-hidden="true">{open ? "▾" : "▸"}</span>
          <span class="closures-toggle-title">Closure days</span>
          <span class="closures-count">{summary}</span>
        </button>
      </h3>

      {open && (
        <div class="closures-body">
          <p class="closures-intro">
            Flag a day to mark it as a company-wide closure. Everyone sees
            a striped marker on the day plus a heads-up banner for closures
            in the next 14 days.
          </p>

          <form class="closures-form" onSubmit={add}>
        <label class="closures-field closures-field-day">
          <span>From</span>
          <input
            type="date"
            value={day}
            onInput={(e) => setDay((e.target as HTMLInputElement).value)}
            required
          />
        </label>
        <label class="closures-field closures-field-day">
          <span>To <span class="closures-opt">(optional)</span></span>
          <input
            type="date"
            value={endDay}
            min={day || undefined}
            onInput={(e) => setEndDay((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="closures-field closures-field-label">
          <span>Label (optional)</span>
          <input
            type="text"
            value={label}
            onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
            maxLength={80}
            placeholder="e.g. Festa della Repubblica · Christmas closure"
          />
        </label>
        <button type="submit" disabled={busy} class="closures-add-btn">
          {busy ? "Saving…" : "+ Add closure"}
        </button>
      </form>
      <p class="closures-form-hint">
        Leave <b>To</b> blank for a single day, or set it to flag a whole
        period at once — e.g. a closure week. Every day in the range
        (weekends included) is marked.
      </p>

      {upcoming.length > 0 && (
        <div class="closures-section">
          <h4 class="closures-section-title">Upcoming</h4>
          <ul class="closures-list">
            {upcoming.map((c) => (
              <li key={c.day} class="closures-row">
                <span class="closures-day">{formatDayLabel(c.day)}</span>
                {c.label && <span class="closures-label">{c.label}</span>}
                <button
                  type="button"
                  class="closures-remove"
                  onClick={() => void remove(c.day)}
                  aria-label={`Remove closure on ${formatDayLabel(c.day)}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {past.length > 0 && (
        <div class="closures-section">
          <h4 class="closures-section-title">Past</h4>
          <ul class="closures-list closures-list-past">
            {past.slice(0, 12).map((c) => (
              <li key={c.day} class="closures-row">
                <span class="closures-day">{formatDayLabel(c.day)}</span>
                {c.label && <span class="closures-label">{c.label}</span>}
                <button
                  type="button"
                  class="closures-remove"
                  onClick={() => void remove(c.day)}
                  aria-label={`Remove past closure on ${formatDayLabel(c.day)}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          {past.length > 12 && (
            <div class="closures-more">+ {past.length - 12} older closures (not shown)</div>
          )}
        </div>
      )}

          {upcoming.length === 0 && past.length === 0 && (
            <div class="closures-empty">
              No closures yet. Add the first one above and it'll appear on
              everyone's My Calendar within a few hundred milliseconds.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
